import React, { useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useAuthStore, useModalStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { availableCredits } from '@/lib/availableCredits';
import Button from '@/components/Buttons/Button';
import BusinessCreditSummary from '@/components/Business/BusinessCreditSummary';
import WorkCreditSummary, { creditCardStyles } from './WorkCreditSummary';

const isContinue = label => /^(?:continue|resume(?: work)?|try again|approve(?: spend)?|i(?:'ll| will) add credits.*|continue after purchase.*)$/i.test(label);
const isCancel = label => /^(?:cancel|don't start|stop)$/i.test(label);

export default function WorkCreditApprovalCard({ question, options, onAnswer, isSubmitting, pendingKey }) {
  const { theme } = useTheme();
  const styles = creditCardStyles(isDarkTheme(theme));
  const credits = useAuthStore(s => s.user?.credits);
  const { openModal } = useModalStore(s => s.actions);
  const available = availableCredits(credits);
  const known = Number.isFinite(available);
  const required = Math.max(0, Math.ceil(Number(question.estimatedCredits) || 0));
  const ready = known && available > 0 && (!required || available >= required);
  const business = !!credits?.business;
  const held = !business && credits?.reservedCredits > 0;
  const refreshInFlight = useRef(null);

  const refresh = useCallback(() => {
    if (!refreshInFlight.current) {
      refreshInFlight.current = Promise.resolve().then(() => useAuthStore.getState().fetchUserCredits({ force: true }))
        .catch(() => false).finally(() => { refreshInFlight.current = null; });
    }
    return refreshInFlight.current;
  }, []);
  useEffect(() => {
    // Paused tasks no longer receive the running-task balance poll.
    refresh();
    const timer = setInterval(refresh, 4000);
    return () => clearInterval(timer);
  }, [refresh]);

  const buy = () => openModal('CreditsPurchaseModal', { initialTab: 'buy-credits', workContext: true, requiredCredits: required || null, returnToWork: true });
  const answer = option => onAnswer(option.label, option.id, option.id || option.label);
  // Preserve persisted answer IDs, and keep any nonstandard choices offered by
  // the task (for example a cheaper model) instead of replacing those choices.
  const standard = options.every(option => isContinue(option.label) || isCancel(option.label));
  const continueOption = options.find(option => isContinue(option.label)) || (options.length === 0 ? { label: 'Continue' } : null);
  const cancelOption = options.find(option => isCancel(option.label)) || (options.length === 0 ? { label: 'Cancel' } : null);
  const blocked = isSubmitting;

  return <View testID="work-credit-approval" style={styles.card}>
    {business && !ready ? <BusinessCreditSummary credits={credits} /> : <WorkCreditSummary credits={credits} requiredCredits={required} blockedByHold={held && !ready}
      title={ready ? 'Ready to continue' : known ? 'Buy credits to continue' : 'Checking credits'}
      description={ready ? 'Your available credits cover this estimate.' : known ? 'Add credits to continue. Your progress is saved.' : 'Your balance will appear here shortly.'} />}
    <View style={styles.actions}>
      {standard ? <>
        {ready && continueOption ? <Button text="Continue" variant="contrast" size="medium" style={styles.button}
          onPress={() => answer(continueOption)} isDisabled={blocked} isLoading={isSubmitting && pendingKey === (continueOption.id || continueOption.label)} />
          : !ready && !business && <Button text={known ? 'Buy credits' : 'Checking…'} variant="contrast" size="medium" style={styles.button}
            onPress={buy} isDisabled={blocked || !known} isLoading={!known} />}
        {cancelOption && <Button text={cancelOption.label} variant="secondary" size="medium" style={[styles.button, styles.secondaryButton]} textStyle={styles.primary}
          onPress={() => answer(cancelOption)} isDisabled={blocked} isLoading={isSubmitting && pendingKey === (cancelOption.id || cancelOption.label)} />}
      </> : options.map((option, index) => <Button key={option.id || index} text={option.label} variant={index === 0 ? 'contrast' : 'secondary'}
        size="medium" style={[styles.button, index > 0 && styles.secondaryButton]} textStyle={index > 0 ? styles.primary : undefined}
        onPress={() => answer(option)} isDisabled={blocked} isLoading={pendingKey === (option.id || option.label)} />)}
      {!standard && !ready && known && !business && <Button text="Buy credits" variant="contrast" size="medium" style={styles.button} onPress={buy} isDisabled={blocked} />}
    </View>
  </View>;
}
